import { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { PropsWithChildren } from "react";
import { getAuthToken } from "@/shared/lib/api";
import { fetchCurrentSession } from "@/shared/lib/supabase";
import type { AuthUser, TailorProfile } from "@/features/auth/types";

type AuthContextValue = {
  configured: boolean;
  loading: boolean;
  authenticated: boolean;
  tailor: TailorProfile | null;
  authUser: AuthUser;
  isOwner: boolean;
  refreshTailor: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: PropsWithChildren) {
  const [loading, setLoading] = useState(true);
  const [tailor, setTailor] = useState<TailorProfile | null>(null);
  const [authUser, setAuthUser] = useState<AuthUser>(null);
  const [authenticated, setAuthenticated] = useState(!!getAuthToken());

  const refreshTailor = async () => {
    const token = getAuthToken();
    if (!token) {
      setTailor(null);
      setAuthUser(null);
      setAuthenticated(false);
      return;
    }

    try {
      const session = await fetchCurrentSession();
      setTailor(session?.tailor ?? null);
      setAuthUser(session?.authUser ?? null);
      setAuthenticated(!!session?.tailor);
    } catch {
      setTailor(null);
      setAuthUser(null);
      setAuthenticated(false);
    }
  };

  useEffect(() => {
    let mounted = true;

    void refreshTailor()
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  const value = useMemo(
    () => ({
      configured: true,
      loading,
      authenticated,
      tailor,
      authUser,
      isOwner: authUser === null,
      refreshTailor
    }),
    [loading, authenticated, tailor, authUser]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// AuthProvider and useAuth intentionally live together so consumers cannot import the raw context.
// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used inside AuthProvider");
  }

  return context;
}
